<template>
  <div class="w-full max-w-2xl p-4 mx-auto mb-10 sm:mt-0 sm:h-auto sm:px-0">
    <div class="h-full overflow-hidden bg-white rounded-lg shadow-lg shadow-gray-200/80 dark:bg-gray-900 dark:shadow-gray-900/50">
      <slot name="header"></slot>

      <div class="p-4 sm:p-8">
        <!-- Title -->
        <div v-if="taskType != 'fillTheBlanks'" class="flex justify-between my-3">
          <h3 class="font-medium leading-6 text-gray-900 text-md dark:text-white md:text-xl">
            <slot name="title"></slot>
          </h3>
        </div>
        <!-- Instructions -->
        <div v-if="showInstructions" class="flex items-center mb-3">
          <div class="flex-shrink-0">
            <InformationCircleIcon class="w-6 h-6 text-blue-700 dark:text-blue-300" />
          </div>
          <div class="flex-1 ml-1 md:flex md:justify-between">
            <div class="text-sm text-blue-700 dark:text-blue-300">
              <div v-show="type == 'sequence'">Drag & drop into right order.</div>
              <div v-show="type == 'fillTheBlanks'">Fill in the blank(s).</div>
              <div v-show="type == 'multipleAnswer'">Select <u>all</u> that apply.</div>
              <div v-show="type == 'activeRecall'">
                This is a active recall task.
                <button class="underline" @click="showSlideOver = true">Learn more.</button>
              </div>
            </div>
          </div>
        </div>

        <!-- Task -->
        <div>
          <slot name="task"></slot>
        </div>

        <!-- Correct Answer -->
        <div>
          <slot name="correctAnswer"></slot>
        </div>

        <!-- Feedback -->
        <div>
          <slot class="" name="feedback"></slot>
        </div>

        <!-- Next -->
        <div>
          <slot name="next"></slot>
        </div>
      </div>
    </div>
  </div>

  <SlideOver :isOpen="showSlideOver" @closed="hideSlideOver">
    <template v-slot:title>Active Recall</template>
    <template v-slot:content>
      <div class="space-y-10">
        <div>
          <div class="block font-bold text-gray-900 dark:text-white">What is it?</div>
          <div class="mt-1">
            <div class="text-gray-800 dark:text-gray-300">
              <div>
                Active recall is the process of remembering information. It is an efficient way of moving information from short-term to long-term memory so that you can easily draw on it again when
                you need it.
              </div>
            </div>
          </div>
        </div>

        <div>
          <div class="block font-bold text-gray-900 dark:text-white">Feynman technique</div>
          <div class="mt-1">
            <div class="text-gray-800 dark:text-gray-300">
              <div>A popular application of active recall is the Feynman technique - explain/teach what you read to a 12 year old.</div>
            </div>
          </div>
        </div>

        <div>
          <div class="block font-bold text-gray-900 dark:text-white">The science</div>
          <div class="mt-1">
            <div class="text-gray-800 dark:text-gray-300">
              Key studies:
              <ul class="mt-1 text-sm list-disc list-inside">
                <li>
                  <a class="text-blue-500 underline" href="https://www.researchgate.net/publication/5574966_The_Critical_Importance_of_Retrieval_for_Learning"
                    >The critical importance of retrieval for learning</a
                  >
                </li>
                <li><a class="text-blue-500 underline" href="https://pubmed.ncbi.nlm.nih.gov/20804289/">Repeated testing produces superior transfer of learning relative to repeated studying</a></li>
                <li>
                  <a class="text-blue-500 underline" href="https://www.science.org/doi/abs/10.1126/science.1199327"
                    >Retrieval Practice Produces More Learning than Elaborative Studying with Concept Mapping7</a
                  >
                </li>
              </ul>
            </div>
          </div>
          <div>
            <div class="mt-4 text-gray-800 dark:text-gray-300">Studies tl;dr:</div>
            <div class="mt-1">
              Retrieving information using active recall helps you remember it. Actively recalling information is much better than simply recognizing an answer, re-reading information, note-taking, or
              concept mapping.
            </div>
          </div>
        </div>
      </div>
    </template>
  </SlideOver>
</template>

<script>
import { InformationCircleIcon } from "@heroicons/vue/outline";
import SlideOver from "@/components/SlideOver.vue";
import { ref } from "vue";
export default {
  props: {
    type: String,
  },
  components: {
    InformationCircleIcon,
    SlideOver,
  },
  setup(props) {
    const showInstructions = ref(false);
    const showSlideOver = ref(false);
    const taskType = ref(props.type);
    const tasksWithoutInstructions = ["trueFalse", "textShort", "multipleChoice"];

    showInstructions.value = tasksWithoutInstructions.includes(taskType.value) ? false : true;

    function hideSlideOver() {
      showSlideOver.value = false;
    }

    return { showInstructions, showSlideOver, hideSlideOver, taskType };
  },
};
</script>
